// A connected SDK iframe cannot be moved without reloading on Safari and older
// Chromium. Keep its renderer in place and compose the dialog's modality around
// both roots for the lifetime of the requested guide.
const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex='-1'])"

export function setupExternalModal(dialog, guide) {
  const previous = document.activeElement
  const previousAriaModal = dialog.getAttribute("aria-modal")
  const previousAriaOwns = dialog.getAttribute("aria-owns")
  const madeInert = new Set()

  dialog.close()
  dialog.show()
  guide.id ||= `${dialog.id}-appcues-guide`

  const owns = (element) => dialog.contains(element) || guide.contains(element)
  // Our dialog is the only modeless app dialog. Other open app dialogs retain
  // native modality; avoid :modal, unsupported on admitted Chrome 98 clients.
  const anotherDialogOpen = () =>
    [...document.querySelectorAll("dialog[open]")].some(
      (other) => other !== dialog,
    )

  const sync = () => {
    if (dialog.getAttribute("aria-modal") !== "true")
      dialog.setAttribute("aria-modal", "true")
    if (dialog.getAttribute("aria-owns") !== guide.id)
      dialog.setAttribute("aria-owns", guide.id)
    const background = new Set()

    const visit = (element) => {
      if (
        element === dialog ||
        element === guide ||
        element.localName === "dialog"
      )
        return
      if (element.contains(dialog) || element.contains(guide)) {
        for (const child of element.children) visit(child)
      } else {
        background.add(element)
        if (!element.inert) {
          element.inert = true
          madeInert.add(element)
        }
      }
    }
    visit(document.body)
    for (const element of madeInert) {
      if (!background.has(element)) {
        element.inert = false
        madeInert.delete(element)
      }
    }
  }

  const onKeydown = (event) => {
    if (anotherDialogOpen() || !owns(document.activeElement)) return
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopImmediatePropagation()
      dialog.dispatchEvent(new Event("cancel", { cancelable: true }))
    } else if (event.key === "Tab") {
      const controls = [dialog, guide].flatMap((root) =>
        [...root.querySelectorAll(FOCUSABLE)].filter(
          (element) =>
            element.tabIndex >= 0 &&
            element.getClientRects().length > 0 &&
            !element.inert,
        ),
      )
      const first = controls[0]
      const last = controls.at(-1)
      if (event.shiftKey && document.activeElement === first && last) {
        event.preventDefault()
        last.focus({ preventScroll: true })
      } else if (!event.shiftKey && document.activeElement === last && first) {
        event.preventDefault()
        first.focus({ preventScroll: true })
      }
    }
  }

  // The admitted Chrome 98-101 clients predate native inert. Keep pointer and
  // focus containment effective there as well; newer browsers use inert too.
  const onPointer = (event) => {
    if (!anotherDialogOpen() && !owns(event.target)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }
  const onFocus = (event) => {
    if (!anotherDialogOpen() && !owns(event.target)) {
      dialog.querySelector(FOCUSABLE)?.focus({ preventScroll: true })
    }
  }

  sync()
  document.addEventListener("keydown", onKeydown, true)
  document.addEventListener("pointerdown", onPointer, true)
  document.addEventListener("click", onPointer, true)
  document.addEventListener("focusin", onFocus, true)
  if (previous?.isConnected && owns(previous))
    previous.focus({ preventScroll: true })

  return {
    sync,
    destroy({ restoreNative = true } = {}) {
      document.removeEventListener("keydown", onKeydown, true)
      document.removeEventListener("pointerdown", onPointer, true)
      document.removeEventListener("click", onPointer, true)
      document.removeEventListener("focusin", onFocus, true)
      for (const element of madeInert) element.inert = false
      madeInert.clear()
      for (const [attribute, value] of [
        ["aria-modal", previousAriaModal],
        ["aria-owns", previousAriaOwns],
      ]) {
        if (value === null) dialog.removeAttribute(attribute)
        else dialog.setAttribute(attribute, value)
      }
      if (restoreNative && dialog.isConnected && dialog.open) {
        const active = document.activeElement
        dialog.close()
        dialog.showModal()
        if (dialog.contains(active)) active.focus({ preventScroll: true })
      }
    },
  }
}
